/* ================================================================
   assets/mif-scale.js — v2.0
   Mighty Imperial Forge · Unified viewport scaling utilities.

   Loaded synchronously by theme.liquid (before #header-group is
   scaled) so window.MifScale is available to all subsequent inline
   scripts on every page (header, collection frame, product frame).

   PUBLIC API  (window.MifScale)
   ─────────────────────────────
   isDesktop(threshold)    Correct mobile / desktop-view detection.
   getBaseVW()             Frozen max viewport width in CSS px.
   updateBaseVW(w)         Call on resize with window.innerWidth.
   setMinWidth(s)          Syncs html min-width + overflow-x.
   scaleGlobal(el, thr)    Scales header / footer / announcement bar.
   centerPageScroll()      Centres page horizontally when zoomed in.
   debouncedCenter()       250 ms debounced version of the above.

   window.centerPageScroll is also set for legacy callers.
   ================================================================ */
(function (global) {
  'use strict';

  var DESIGN  = 1920;
  var VW_KEY  = 'mif-base-vw';
  var _screenW = screen.width || 0;

  /* In-app browsers (Messenger, Instagram, Facebook) should never
     receive CSS zoom scaling — their WebView engines handle it
     unpredictably.  The mif-webview class is set in theme.liquid. */
  var _isWebView = document.documentElement.classList.contains('mif-webview');

  /* ── baseVW ──────────────────────────────────────────────────────────
     Frozen max CSS viewport width.  Prevents the scale factor from
     jumping when the user presses Ctrl + (browser zoom shrinks
     window.innerWidth, but our scale stays fixed at the baseline).

     CONTAMINATION GUARD: we only read sessionStorage when screen.width
     indicates a real desktop (> 1024 px).  This blocks the common
     failure mode where a prior desktop visit stores 1920 px and a
     phone session later reads that value, forcing s = 1.0 and
     rendering a full-scale 1920 px layout on a 390 px screen.
     ──────────────────────────────────────────────────────────────────── */
  var baseVW = window.innerWidth;
  if (_screenW > 1024) {
    try {
      var _s = Number(sessionStorage.getItem(VW_KEY)) || 0;
      if (_s > baseVW) baseVW = _s;
    } catch (e) {}
  }

  /* ── isDesktop(threshold) ────────────────────────────────────────────
     THE canonical mobile-vs-desktop check for every MIF page.

     WHY window.innerWidth ALONE (not Math.max with screen.width):
     After the viewport meta tag is applied (which theme.liquid now
     places before any script), window.innerWidth is ALWAYS the CSS
     layout viewport width:
       · Native mobile  ~390–430 px  → correctly below any threshold
       · Request Desktop Site  ~980–1280 px  → correctly above threshold
     screen.width is unreliable on Android where it reports physical
     device pixels (e.g. 1080 px on a 390 px CSS viewport phone).
     Using Math.max(_screenW, window.innerWidth) = Math.max(1080, 390)
     = 1080 which is ALWAYS above 768, permanently treating Android
     native mobile as desktop.  window.innerWidth alone is correct.
     ──────────────────────────────────────────────────────────────────── */
  function isDesktop(threshold) {
    return window.innerWidth > threshold;
  }

  function getBaseVW() { return baseVW; }

  /* Only persist on real desktops so a phone session can never inflate
     the stored value and corrupt a subsequent desktop session's scale. */
  function updateBaseVW(w) {
    if (w > baseVW) {
      baseVW = w;
      if (_screenW > 1024) {
        try { sessionStorage.setItem(VW_KEY, String(baseVW)); } catch (e) {}
      }
    }
  }

  /* ── setMinWidth(s) ──────────────────────────────────────────────────
     Keeps the <html> element at least as wide as the scaled content so
     margin:auto always centres correctly regardless of scrollbar width.
     overflow-x:hidden at 100 % zoom (no spurious scrollbar on normal
     desktop); overflow-x:auto when browser-zoomed-in (horizontal scroll
     lets the user pan across the full 1920 px design).
     ──────────────────────────────────────────────────────────────────── */
  function setMinWidth(s) {
    var doc = document.documentElement;
    doc.style.setProperty('min-width', Math.ceil(DESIGN * s) + 'px', 'important');
    var zoomed = baseVW > window.innerWidth * 1.03;
    doc.style.setProperty('overflow-x', zoomed ? 'auto' : 'hidden', 'important');
  }

  /* ── scaleGlobal(container, threshold) ──────────────────────────────
     Scales every visible child of container with CSS zoom (not
     transform:scale, so layout dimensions update naturally).
     Used for the site header, footer, and announcement bar.

     Desktop / desktop-view:
       Every child → width: 1920 px; margin: 0 auto; zoom: s.
     Mobile native (isDesktop returns false):
       Strips all inline scaling so the browser's own responsive CSS
       takes uncontested control.
     ──────────────────────────────────────────────────────────────────── */
  function scaleGlobal(container, threshold) {
    if (!container) return;
    /* Skip all zoom scaling in WebViews — let native mobile CSS handle it */
    if (_isWebView) return;
    var ch = [];
    for (var i = 0; i < container.children.length; i++) {
      var tag = container.children[i].tagName;
      if (tag !== 'SCRIPT' && tag !== 'STYLE') ch.push(container.children[i]);
    }

    if (!isDesktop(threshold)) {
      for (var j = 0; j < ch.length; j++) {
        ch[j].style.removeProperty('width');
        ch[j].style.removeProperty('zoom');
        ch[j].style.removeProperty('left');
        ch[j].style.removeProperty('transform');
        ch[j].style.removeProperty('margin-left');
        ch[j].style.removeProperty('margin-right');
      }
      document.documentElement.style.removeProperty('min-width');
      document.documentElement.style.removeProperty('overflow-x');
      return;
    }

    var s = Math.min(1, baseVW / DESIGN);
    /* min-width keeps margin:auto centring correct on all desktop
       viewports.  On narrow desktop (RDS) the computed min-width
       equals the viewport width (ceil(1920 * s) ≈ baseVW), so it
       introduces no overflow or scroll fighting — it just anchors
       the centering reference, same as wide desktop. */
    setMinWidth(s);

    for (var k = 0; k < ch.length; k++) {
      ch[k].style.setProperty('width',        DESIGN + 'px', 'important');
      ch[k].style.setProperty('margin-left',  'auto',        'important');
      ch[k].style.setProperty('margin-right', 'auto',        'important');
      ch[k].style.removeProperty('left');
      ch[k].style.removeProperty('transform');
      ch[k].style.zoom = s;
    }
  }

  /* ── centerPageScroll() ──────────────────────────────────────────────
     Horizontally centres the page when content is wider than the
     viewport (i.e. the user has Ctrl-zoomed in).  Exposed on window
     so collection and product scripts can call it directly.
     ──────────────────────────────────────────────────────────────────── */
  function centerPageScroll() {
    /* On narrow desktop (phone "Request Desktop Site"), content is
       CSS-zoomed to fit the viewport — no horizontal overflow exists
       and scrollTo would fight the natural centered layout. */
    if (baseVW < 1200) return;
    var docW = document.documentElement.scrollWidth;
    var vpW  = window.innerWidth;
    if (docW > vpW) {
      window.scrollTo(Math.round((docW - vpW) / 2), window.scrollY || window.pageYOffset);
    }
  }

  var _ct = 0;
  function debouncedCenter() {
    clearTimeout(_ct);
    _ct = setTimeout(centerPageScroll, 250);
  }

  /* ── Public API ── */
  global.MifScale = {
    DESIGN          : DESIGN,
    isDesktop       : isDesktop,
    getBaseVW       : getBaseVW,
    updateBaseVW    : updateBaseVW,
    setMinWidth     : setMinWidth,
    scaleGlobal     : scaleGlobal,
    centerPageScroll: centerPageScroll,
    debouncedCenter : debouncedCenter
  };

  /* Preserve window.centerPageScroll for any existing callers */
  global.centerPageScroll = centerPageScroll;

}(window));
